/**
 * DASHBOARD LAYOUT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 *
 * Features:
 * - True glassmorphism with content visible behind panels
 * - Subtle ambient lighting effects
 * - Scroll edge fade effect (per WWDC25)
 * - Smooth page transitions
 * - Responsive sidebar
 */

import { Outlet, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'

export function DashboardLayout() {
  const location = useLocation()

  return (
    <div className="relative h-screen overflow-hidden p-3 bg-light-bg-secondary dark:bg-dark-bg-primary">
      {/* Ambient background with subtle gradient */}
      <div className="absolute inset-0 bg-light-bg-secondary dark:bg-dark-bg-primary" />

      {/* Subtle ambient glow in corner */}
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue/5 dark:bg-blue-dark/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple/5 dark:bg-purple-dark/5 rounded-full blur-3xl pointer-events-none" />

      {/* Sidebar - Positioned over content with glass effect */}
      <aside className="absolute left-3 top-3 bottom-3 w-64 z-20">
        <Sidebar />
      </aside>

      {/* Main content area - Extends under topbar with scroll */}
      <main className="absolute left-[calc(16rem+1.5rem)] right-3 top-3 bottom-3 flex flex-col rounded-2xl overflow-hidden">
        {/* Topbar - Fixed at top with glass effect over scrolling content */}
        <div className="relative z-10">
          <Topbar />
        </div>

        {/* Scrollable content with animated page transitions */}
        <div className="flex-1 overflow-y-auto -mt-[64px] pt-[64px] scrollbar-glass relative">

          {/* Page content with route-based animation */}
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300,
                duration: 0.3
              }}
              className="-mt-6"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  )
}
